import axios from 'axios'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import i18next from 'i18next'
interface StrapiErrorShape {
    error?: {
        name?: string
        message?: string
        details?: { code?: string }
    }
}

function sanitizeUrl(url?: string) {
    if (!url) return undefined

    try {
        const parsed = new URL(url, window.location.origin)
        return parsed.pathname
    } catch {
        return url.split('?')[0]
    }
}

export async function captureApiError(error: unknown) {
    const Sentry = await import('@sentry/vue')

    if (!axios.isAxiosError<StrapiErrorShape>(error)) {
        Sentry.captureException(error)
        return
    }

    const status = error.response?.status
    const method = error.config?.method?.toUpperCase()
    const url = sanitizeUrl(error.config?.url)
    const strapiError = error.response?.data?.error

    Sentry.withScope((scope) => {
        scope.setTag('error.type', 'api')
        scope.setTag('http.method', method ?? 'unknown')
        scope.setTag('http.status_code', String(status ?? 'unknown'))

        scope.setContext('request', { method, url, status })

        if (strapiError) {
            scope.setContext('strapi', {
                name: strapiError.name,
                message: strapiError.message,
            })
        }

        scope.addBreadcrumb({
            category: 'http',
            type: 'http',
            level: status && status >= 500 ? 'error' : 'warning',
            data: { method, url, status },
        })

        scope.captureException(error)
    })
}

export async function captureMissingTranslation(
    message: string,
    namespace: string,
    key: string,
    languages: readonly string[]
) {
    const Sentry = await import('@sentry/vue')

    Sentry.withScope((scope) => {
        scope.setLevel('warning')
        scope.setTag('issue.category', 'i18n.missing_translation')
        scope.setFingerprint(['i18n', 'missing', namespace, key])
        scope.setContext('translation', { key, namespace, languages })
        Sentry.captureMessage(message)
    })
}

export async function captureTranslationLoadError(error: unknown, url?: string, status?: number) {
    const Sentry = await import('@sentry/vue')

    Sentry.captureException(error, { extra: { url, status } })
}

export async function initSentry(app: App, router: Router) {
    const sentryEnvironment = (
        window.appConfig?.environment ||
        import.meta.env.VITE_FRONTEND_SENTRY_ENVIRONMENT ||
        'production'
    ).toLowerCase()
    const sentryEnabled =
        !import.meta.env.DEV && ['dev', 'staging', 'prod'].includes(sentryEnvironment)
    const sentryDsn = import.meta.env.VITE_FRONTEND_SENTRY_DSN

    if (!sentryEnabled || !sentryDsn) return

    const Sentry = await import('@sentry/vue')

    router.afterEach((to) => {
        Sentry.setTag('route.name', String(to.name ?? 'unknown'))
        Sentry.setTag('route.path', to.path)
    })

    Sentry.init({
        app,
        release: import.meta.env.VITE_APP_VERSION || 'unknown',
        environment: sentryEnvironment,
        dsn: sentryDsn,
        // Never attach IP addresses, cookies, or other PII to events
        sendDefaultPii: false,
        tracesSampleRate: sentryEnvironment === 'prod' ? 0.05 : 0.2,
        profilesSampleRate: sentryEnvironment === 'prod' ? 0.01 : 0.1,
        maxValueLength: 5000,
        normalizeDepth: 10,
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.browserProfilingIntegration(),
            Sentry.feedbackIntegration({
                colorScheme: 'system',
                showBranding: false,
                // Injected manually after login — not shown to anonymous users
                autoInject: false,
                triggerLabel: i18next.t('ui.bugReport.triggerLabel'),
                submitButtonLabel: i18next.t('ui.bugReport.submitButtonLabel'),
                cancelButtonLabel: i18next.t('ui.bugReport.cancelButtonLabel'),
                confirmButtonLabel: i18next.t('ui.bugReport.confirmButtonLabel'),
                addScreenshotButtonLabel: i18next.t('ui.bugReport.addScreenshotButtonLabel'),
                removeScreenshotButtonLabel: i18next.t('ui.bugReport.removeScreenshotButtonLabel'),
                formTitle: i18next.t('ui.bugReport.formTitle'),
                nameLabel: i18next.t('ui.bugReport.nameLabel'),
                namePlaceholder: i18next.t('ui.bugReport.namePlaceholder'),
                emailLabel: i18next.t('ui.bugReport.emailLabel'),
                emailPlaceholder: i18next.t('ui.bugReport.emailPlaceholder'),
                messageLabel: i18next.t('ui.bugReport.messageLabel'),
                messagePlaceholder: i18next.t('ui.bugReport.messagePlaceholder'),
                isRequiredLabel: i18next.t('ui.bugReport.isRequiredLabel'),
                successMessageText: i18next.t('ui.bugReport.successMessageText'),
                // Tag the current page URL so the team knows which view the report came from.
                onFormOpen: () => {
                    Sentry.setTag('feedback.url', window.location.href)
                },
                onSubmitError: (error: Error) => {
                    Sentry.captureException(error)
                },
            }),
        ],
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'console' && breadcrumb.message) {
                return {
                    ...breadcrumb,
                    message: breadcrumb.message.slice(0, 500),
                }
            }

            return breadcrumb
        },
        // Only forward Axios errors that indicate a real server/auth problem (403 or >=500)
        // to Sentry; expected 4xx client errors (validation, not-found, etc.) are noise.
        beforeSend(event, hint) {
            if (
                hint.originalException instanceof DOMException &&
                hint.originalException.name === 'AbortError'
            ) {
                return null
            }

            const originalException = hint.originalException as {
                isAxiosError?: boolean
                status?: number
                response?: { status?: number }
            }

            if (!originalException?.isAxiosError) return event
            if (!originalException.response) return event

            const status = originalException.response.status

            if (status === undefined) return event
            if (status >= 500 || status === 403) return event
            return null
        },
    })
}
